import type { ReactNode } from "react";

import {
  MAP_TEXT_LABEL_FONT_OPTIONS,
  UI_TEXT_LABEL_FONT_OPTIONS,
  resolveTextLabelFontStack,
} from "@/lib/textLabelFonts";
import { TEXT_LABEL_SIZE_OPTIONS } from "@/lib/textLabelSizes";

function SectionTitle({ children, id }: { children: ReactNode; id: string }) {
  return (
    <h2
      id={id}
      className="scroll-mt-6 text-xl font-semibold uppercase tracking-[0.2em] [font-family:var(--color-lab-display),system-ui,sans-serif]"
    >
      {children}
    </h2>
  );
}

/* ---------------------------------------------------------------------------
 * Page
 * -------------------------------------------------------------------------*/

export default function FontsPage() {
  const mapFont = MAP_TEXT_LABEL_FONT_OPTIONS[0]!;
  const uiFont = UI_TEXT_LABEL_FONT_OPTIONS[0]!;

  return (
    <div className="mx-auto min-h-full max-w-5xl px-5 py-12 pb-24 text-neutral-950 sm:px-8">
      <header className="border-b-[3px] border-neutral-950 pb-10">
        <p className="text-[11px] uppercase tracking-[0.28em] text-neutral-600">
          Design system · Typography
        </p>
        <h1 className="mt-4 text-4xl font-bold leading-tight tracking-tight [font-family:var(--color-lab-display),system-ui,sans-serif] sm:text-5xl">
          Type system
        </h1>
        <p className="mt-4 max-w-2xl text-lg leading-relaxed text-neutral-800">
          Typography is now split into two clear roles: <strong className="text-neutral-950">Fondamento</strong> is
          the single handwritten font used for map labels, and <strong className="text-neutral-950">Classic</strong>{" "}
          is the readable UI font used for controls, panels, and application copy.
        </p>
        <nav className="mt-8 flex flex-wrap gap-3 text-sm">
          <a className="underline decoration-2 underline-offset-4 hover:text-neutral-700" href="#map-text">
            Map text
          </a>
          <a className="underline decoration-2 underline-offset-4 hover:text-neutral-700" href="#ui-text">
            UI text
          </a>
          <a className="underline decoration-2 underline-offset-4 hover:text-neutral-700" href="#licensing">
            Licensing
          </a>
        </nav>
      </header>

      <div className="mt-14 space-y-20">
        <section className="space-y-6" aria-labelledby="map-text">
          <SectionTitle id="map-text">Map text</SectionTitle>
          <p className="max-w-2xl text-neutral-800">
            All labels placed directly on the map use <strong className="text-neutral-950">{mapFont.label}</strong>.
            There is no longer a font chooser for map labels. Instead, the text toolbar exposes seven size presets:
            <strong className="text-neutral-950"> 4XL, 3XL, 2XL, XL, L, M, and S</strong>.
          </p>

          <div className="grid gap-6">
            {TEXT_LABEL_SIZE_OPTIONS
              .slice()
              .reverse()
              .map((tier) => (
              <article
                key={tier.id}
                className="overflow-hidden border-[3px] border-neutral-950 bg-[#fffaf1] shadow-[10px_10px_0_rgba(19,19,19,0.12)]"
              >
                <div className="flex items-center justify-between border-b-[3px] border-neutral-950 bg-neutral-950 px-5 py-3 sm:px-6">
                  <div className="flex items-center gap-3">
                    <span className="text-[10px] font-semibold uppercase tracking-[0.28em] text-white/60">
                      {tier.title}
                    </span>
                    <span className="rounded-full border border-white/20 bg-white/8 px-2.5 py-0.5 text-[10px] uppercase tracking-[0.2em] text-white/75">
                      {tier.label}
                    </span>
                  </div>
                  <span className="text-[10px] uppercase tracking-[0.2em] text-white/45">{tier.fontSize}px</span>
                </div>

                <div className="grid gap-0 lg:grid-cols-[1fr_1.2fr]">
                  <div className="flex flex-col justify-center border-b-[3px] border-neutral-950 p-5 sm:p-6 lg:border-b-0 lg:border-r-[3px]">
                    <p className="text-sm leading-relaxed text-neutral-800">{tier.description}</p>
                  </div>
                  <div className="flex items-center bg-[linear-gradient(180deg,#1b2129,#0f141b)] px-5 py-8 sm:px-8">
                    <div
                      className={`leading-none ${tier.className}`}
                      style={{
                        color: tier.color,
                        fontFamily: resolveTextLabelFontStack(mapFont.id),
                        textShadow: "0 2px 10px rgba(0, 0, 0, 0.55)",
                      }}
                    >
                      {tier.sampleText}
                    </div>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </section>

        <section className="space-y-6" aria-labelledby="ui-text">
          <SectionTitle id="ui-text">UI text</SectionTitle>
          <p className="max-w-2xl text-neutral-800">
            The application interface uses <strong className="text-neutral-950">{uiFont.label}</strong>, the system
            sans stack. This is the readable font for form fields, dialogs, buttons, and management screens. It is not
            offered in the map text picker.
          </p>

          <article className="overflow-hidden border-[3px] border-neutral-950 bg-[#fffaf1] shadow-[10px_10px_0_rgba(19,19,19,0.12)]">
            <div className="border-b-[3px] border-neutral-950 bg-neutral-950 px-6 py-5 text-white sm:px-8">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <p className="text-[11px] uppercase tracking-[0.26em] text-white/60">{uiFont.label}</p>
                  <p className="mt-2 max-w-2xl text-sm text-white/78">{uiFont.note}</p>
                </div>
                <div className="rounded-full border border-white/20 bg-white/8 px-3 py-1 text-[10px] uppercase tracking-[0.24em] text-white/75">
                  Preset id: {uiFont.id}
                </div>
              </div>
            </div>

            <div className="grid gap-6 p-6 sm:p-8 lg:grid-cols-2">
              <div className="rounded-2xl border border-neutral-950/10 bg-white px-5 py-4">
                <div className="text-[10px] uppercase tracking-[0.24em] text-neutral-500">Button text</div>
                <div
                  className="mt-3 inline-flex rounded-xl border-2 border-neutral-950 bg-neutral-950 px-4 py-2 text-sm font-semibold text-[#fff7ec]"
                  style={{ fontFamily: resolveTextLabelFontStack(uiFont.id) }}
                >
                  Save changes
                </div>
              </div>
              <div className="rounded-2xl border border-neutral-950/10 bg-white px-5 py-4">
                <div className="text-[10px] uppercase tracking-[0.24em] text-neutral-500">Panel copy</div>
                <div style={{ fontFamily: resolveTextLabelFontStack(uiFont.id) }}>
                  <h3 className="mt-3 text-lg font-semibold text-neutral-950">Map note</h3>
                  <p className="mt-2 text-sm leading-relaxed text-neutral-700">
                    Clear, readable UI text for metadata, controls, and longer descriptions around the map.
                  </p>
                </div>
              </div>
            </div>
          </article>
        </section>

        <section className="space-y-6" aria-labelledby="licensing">
          <SectionTitle id="licensing">Licensing</SectionTitle>
          <p className="max-w-2xl text-neutral-800">
            Both active fonts are free for commercial use. Classic is the system stack; Fondamento is the bundled
            Google Font used for map labels.
          </p>

          <div className="border-[3px] border-neutral-950 bg-[#fffaf1] shadow-[10px_10px_0_rgba(19,19,19,0.12)]">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b-[3px] border-neutral-950 bg-neutral-950 text-left text-[10px] uppercase tracking-[0.24em] text-white/60">
                  <th className="px-5 py-3 font-semibold sm:px-6">Font</th>
                  <th className="px-5 py-3 font-semibold sm:px-6">License</th>
                </tr>
              </thead>
              <tbody>
                {[
                  { font: "Classic (system fonts)", license: "Ships with the OS" },
                  { font: "Fondamento", license: "SIL Open Font License 1.1" },
                ].map((row, i, arr) => (
                  <tr
                    key={row.font}
                    className={i < arr.length - 1 ? "border-b-2 border-neutral-950/15" : ""}
                  >
                    <td className="px-5 py-3 font-medium text-neutral-950 sm:px-6">{row.font}</td>
                    <td className="px-5 py-3 text-neutral-700 sm:px-6">{row.license}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      </div>
    </div>
  );
}
